/**
 * ClientNetWorld - NetWorld implementation for connected game clients.
 * Receives replication packets, routes RPCs, sends heartbeats. Server is authoritative.
 */
import { SceneComponent } from '../components/SceneComponent.js';
import { createLoader } from '../utils/serialization/factories.js';

import { createLagSimulatingConnectionFactory } from './implementations/LagSimulatingNetworkTransport.js';
import { NetworkGameSession, WebSocketNetworkConnection } from './implementations/NetworkGameSession.js';
import {
  type ActorAck,
  type ClientId,
  type IncomingRpc,
  type ObjectId,
  type ReplicationAck,
  type ReplicationPacket
} from './interface.js';
import { getDeviceId } from './NetIdentity.js';
import { type NetClientConnectOptions, NetWorld } from './NetWorld.js';
import { BinaryReader } from './ReplicationBinaryCodec.js';
import { REPLICATION_COMPONENTS_KEY, ReplicationSerializer } from './ReplicationSerializer.js';
import { decodeRpcArgs, encodeRpcArgs } from './RpcBinaryCodec.js';

import type { Actor } from '../actors/Actor.js';
import type { NetPropertyMetadata } from '../utils/serialization/decorator.js';
import type { SerializedData } from '../utils/serialization/types.js';


/**
 * Fully-encoded RPC accumulated during a tick and flushed to the wire at the end of the tick.
 */
interface OutboundServerRPC {
  objectId: ObjectId;
  methodName: string;
  argsPayload: Uint8Array;
  reliable: boolean;
  netSubId?: string;
}

/**
 * `NetWorld` subclass for player-facing client instances connected to a remote server.
 */
export class ClientNetWorld extends NetWorld<NetworkGameSession> {
  /** Transport ID → Actor mapping for incoming replication packets. */
  protected transportIdToActor: Map<number, Actor> = new Map();

  /**
   * Last replication-packet tick successfully applied per transport ID.
   * Out-of-order or duplicate packets with a tick ≤ this value are dropped.
   */
  private lastAppliedTick: Map<number, number> = new Map();

  /** Outbound RPCs accumulated this tick; flushed to the wire at the end of tick(). */
  private outboundRPCQueue: OutboundServerRPC[] = [];

  /**
   * Per-actor sliding window of received server-confirmed state snapshots.
   * transportId → (tick → snapshot). Captures exactly what was on the wire
   * (incl. unresolved actor-ref UUIDs) rather than the (possibly predicted)
   * live actor properties.
   */
  private receivedSnapshots: Map<number, Map<number, Record<string, unknown>>> = new Map();

  /**
   * Per-actor received-snapshot window size. Must be at least 2 (one slot is
   * permanently occupied by the pinned baseTick snapshot).
   *
   * MUST equal `ClientActorState.MAX_SNAPSHOTS` on the server. The stale-middle
   * eviction logic (see handleReplicationPacket) relies on both sides having the
   * same window size to determine which snapshots the server has already evicted:
   * any client snapshot more than MAX_CLIENT_SNAPSHOTS_PER_ACTOR ticks behind the
   * incoming tick is guaranteed to be gone from the server's ring buffer too.
   */
  private static readonly MAX_CLIENT_SNAPSHOTS_PER_ACTOR = 16;

  // ---------------------------------------------------------------------------
  // Dangling actor-reference tracking (two-index structure)
  //
  // danglingForward:  ownerUuid → (dotPath → referencedUuid)
  //   Used for O(1) cleanup when an owner actor is destroyed, and to detect
  //   superseded references (same dotPath updated to point at a different actor).
  //
  // danglingInverted: referencedUuid → (ownerUuid → Set<dotPath>)
  //   Used for O(1) lookup when a new actor spawns — only the entries that
  //   reference the newly spawned UUID need to be checked.
  // ---------------------------------------------------------------------------

  private danglingForward: Map<string, Map<string, string>> = new Map();
  private danglingInverted: Map<string, Map<string, Set<string>>> = new Map();

  /**
   * Builds a {@link NetworkGameSession}, awaits handshake readiness, and attaches it to this world.
   */
  public async connectToServer(options: NetClientConnectOptions): Promise<void> {
    const lagSimulationConfig = options.lagSimulationConfig;
    const session = new NetworkGameSession({
      serverUrl: options.serverUrl,
      connectionFactory: lagSimulationConfig?.enabled
        ? createLagSimulatingConnectionFactory((url) => new WebSocketNetworkConnection(url), lagSimulationConfig)
        : undefined
    });
    await session.waitUntilReady();
    await this.attachGameSession(session);
  }

  /**
   * Flush any queued outbound RPCs, then refresh the network stats overlay.
   * @param _deltaTime - Unused.
   */
  public override async tick(_deltaTime: number): Promise<void> {
    this.flushOutboundRPCQueue();
    this.updateNetStats(0);
  }

  /**
   * Wires up client-side session handlers once a session has been assigned.
   * Hooks: session lifecycle, incoming RPCs, incoming replication packets,
   * console commands, and a periodic heartbeat.
   */
  protected override async onGameSessionSet(): Promise<void> {
    this.flushPendingNetObjects();

    const session = this.gameSession!;

    session.onPlayerJoined.add(this.onSessionPlayerJoined.bind(this));
    session.onPlayerLeft.add(this.onSessionPlayerLeft.bind(this));
    session.onSessionStateChanged.add(this.onSessionStateChanged.bind(this));
    session.onRPCReceived.add(this.handleIncomingRpc.bind(this));
    session.onReplicationPacketReceived.add(this.handleReplicationPacket.bind(this));

    // Make this instance available for console commands (e.g. disconnect, stats).
    this.world.consoleManager.addCommandHandler(this);

    // Initial heartbeat so the server registers this client immediately.
    await this.sendHeartbeat();

    // Periodic heartbeat to keep the connection alive.
    this.startHeartbeatTimer();
  }

  /**
   * Send RPC from client to server.
   * Resolves param metadata, encodes args to binary, then enqueues for end-of-tick flush.
   */
  public override sendServerRPC(
    objectId: ObjectId,
    methodName: string,
    args: unknown[],
    reliable: boolean,
    netSubId?: string
  ): void {
    if (!this.gameSession) {
      console.warn('[ClientNetWorld] Cannot send server RPC: no game session');
      return;
    }

    const actor = this.getLocalActorByObjectId(objectId);
    let params: NetPropertyMetadata[] | null;
    if (netSubId && actor) {
      const component = actor.getComponents(SceneComponent).find(c => c.uuid === netSubId);
      params = component ? this.getRpcParamsForMethod(component.constructor, methodName) : null;
    } else {
      params = actor ? this.getRpcParamsForMethod(actor.constructor, methodName) : null;
    }

    if (!params) {
      console.error(
        `[ClientNetWorld] RPC "${methodName}" has no param metadata. ` +
        'Add params to the RPC decorator or ensure the property metadata generator has run.',
      );
      return;
    }

    const argsPayload = encodeRpcArgs(args, params);
    this.outboundRPCQueue.push({ objectId, methodName, argsPayload, reliable, netSubId });
  }

  /**
   * Flush all RPCs accumulated this tick to the wire.
   * Called at the end of `tick()`, after stats are refreshed.
   */
  private flushOutboundRPCQueue(): void {
    if (!this.gameSession || this.outboundRPCQueue.length === 0) {
      return;
    }
    for (const rpc of this.outboundRPCQueue) {
      this.gameSession.sendServerRpc(rpc.objectId, rpc.methodName, rpc.argsPayload, rpc.reliable, rpc.netSubId);
      this.netStats.numOutgoingRPCs++;
    }
    this.outboundRPCQueue = [];
  }

  /**
   * Decode binary RPC payload and dispatch to the target actor or component.
   * Subscribed to `session.onRPCReceived` in {@link onGameSessionSet}.
   */
  private handleIncomingRpc(message: IncomingRpc, senderId?: ClientId): void {
    const { objectId, methodName, argsPayload, netSubId } = message;

    const actor = this.getLocalActorByObjectId(objectId);
    if (!actor) {
      console.warn(`[ClientNetWorld] RPC target actor not found: ${objectId}`);
      return;
    }

    let params: NetPropertyMetadata[] | null;
    if (netSubId) {
      const component = actor.getComponents(SceneComponent).find(c => c.uuid === netSubId);
      if (!component) {
        console.warn(`[ClientNetWorld] RPC component "${netSubId}" not found on actor ${objectId}`);
        return;
      }
      params = this.getRpcParamsForMethod(component.constructor, methodName);
    } else {
      params = this.getRpcParamsForMethod(actor.constructor, methodName);
    }

    if (!params) {
      console.error(
        `[ClientNetWorld] RPC "${methodName}" has no param metadata — cannot decode. Dropping.`,
      );
      return;
    }

    let args: unknown[];
    try {
      args = decodeRpcArgs(new BinaryReader(argsPayload), params);
    } catch (error) {
      console.error(`[ClientNetWorld] Failed to decode RPC "${methodName}":`, error);
      return;
    }

    this.dispatchRPCCall(actor, methodName, args, netSubId, senderId);
  }

  /**
   * Handle per-actor replication packet from server.
   * Spawns, updates, or destroys actors based on transport-ID-keyed updates,
   * resolving dangling actor references when new actors arrive.
   */
  protected async handleReplicationPacket(packet: ReplicationPacket): Promise<void> {
    const acksToSend: ActorAck[] = [];

    for (const actorUpdate of packet.actors) {
      const { transportId, updateType } = actorUpdate;

      if (updateType === 'spawn') {
        const spawnData = actorUpdate.data;
        try {
          const constructionData: SerializedData | undefined = spawnData.constructionData;

          if (!constructionData) {
            console.error('[ClientNetWorld] Missing construction data in spawn packet');
            continue;
          }

          let constructedActor: Actor;
          try {
            constructedActor = await createLoader().loadAsync(constructionData) as Actor;
          } catch (error) {
            console.error('[ClientNetWorld] Loader failed to construct actor:', error);
            continue;
          }

          if (!constructedActor) {
            console.error('[ClientNetWorld] Failed to construct actor from spawn data');
            continue;
          }

          // Override UUID with the one from server (important for matching scene-placed actors)
          if (spawnData.uuid) {
            (constructedActor as any).uuid = spawnData.uuid;
          }

          // Check if an actor with this UUID already exists (e.g., loaded from scene file)
          const existingActor = this.world.getActors().find(a => a.uuid === constructedActor.uuid);
          if (existingActor) {
            console.log(`[ClientNetWorld] Replacing existing actor ${existingActor.constructor.name} (${existingActor.uuid}) with server version`);

            this.world.removeActors(existingActor);

            const existingNetId = this.getNetId(existingActor);
            if (existingNetId) {
              this.localNetObjects.delete(existingNetId);
            }
          }

          // Set network properties
          constructedActor.netOwningClientId = spawnData.owningClientId ?? null;
          constructedActor.setRemoteNetClientId(spawnData.owningClientId ?? '');
          constructedActor.setRemoteNetDeviceId(getDeviceId());

          // Apply initial binary state and capture the spawn snapshot.
          // Server has already swapped the roles before sending, so we apply directly.
          // onRep callbacks are collected and deferred until after the actor is in the world.
          let spawnPendingCallbacks: Array<[object, string]> | undefined;
          if (spawnData.initialState) {
            const spawnSnapshot: Record<string, unknown> = {};
            spawnPendingCallbacks = [];

            // Use the CDO snapshot as the delta base so the server only sends what
            // actually differs from class defaults (mirrors the server-side encoding).
            const isOwner = spawnData.owningClientId != null && spawnData.owningClientId === this.clientId;
            const cdoSnapshot = ReplicationSerializer.getCdoSnapshot(constructedActor, isOwner);

            const unresolvedRefs = ReplicationSerializer.deserializeIntoActor(
              constructedActor,
              spawnData.initialState,
              cdoSnapshot ?? {},
              spawnSnapshot,
              (uuid) => this.world.getActorByUuid(uuid),
              spawnPendingCallbacks,
            );

            // Track any dangling actor references.
            if (unresolvedRefs.size > 0) {
              for (const [dotPath, referencedUuid] of unresolvedRefs) {
                this.addDanglingRef(constructedActor.uuid, dotPath, referencedUuid);
              }
              console.debug(`[ClientNetWorld] Actor ${constructedActor.constructor.name} has ${unresolvedRefs.size} unresolved references:`, [...unresolvedRefs.keys()]);
            }

            // Store the spawn snapshot so updates can carry it forward correctly.
            this.storeReceivedSnapshot(transportId, packet.tick, -1, spawnSnapshot);
            this.lastAppliedTick.set(transportId, packet.tick);
          }

          this.world.addActors(constructedActor);

          // Fire deferred onRep callbacks now that the actor is live in the world.
          if (spawnPendingCallbacks) {
            for (const [host, cbName] of spawnPendingCallbacks) {
              (host as Record<string, () => void>)[cbName]();
            }
          }

          this.transportIdToActor.set(transportId, constructedActor);

          acksToSend.push({
            transportId,
            ackType: 'spawned'
          });

          // Resolve any existing actors that were waiting for this newly spawned actor.
          this.resolveDanglingReferences(constructedActor.uuid);
        } catch (error) {
          console.error('[ClientNetWorld] Failed to spawn actor from replication packet:', error);
        }
      } else if (updateType === 'update') {
        const actor = this.transportIdToActor.get(transportId);
        if (actor) {
          // Drop stale or duplicate out-of-order packets.
          const lastTick = this.lastAppliedTick.get(transportId) ?? -1;
          if (packet.tick <= lastTick) {
            continue;
          }

          const updateData = actorUpdate.data;

          // Prune snapshots the server will never reference again, then look up
          // exactly the snapshot the server diffed from.
          const window = this.receivedSnapshots.get(transportId);
          if (window) {
            for (const t of window.keys()) {
              if (t < updateData.baseTick) window.delete(t);
            }
          }

          const prevSnapshot = window?.get(updateData.baseTick) ?? null;
          if (prevSnapshot === null) {
            // The server guarantees baseTick is always in-window via its pinned-eviction
            // policy. A missing snapshot here is a protocol invariant violation.
            console.error(
              `[ClientNetWorld] Missing snapshot for baseTick ${updateData.baseTick} ` +
              `(transport ${transportId}) — dropping packet.`
            );
            continue;
          }
          const newSnapshot: Record<string, unknown> = {};
          const pendingCallbacks: Array<[object, string]> = [];

          const unresolvedRefs = ReplicationSerializer.deserializeIntoActor(
            actor,
            updateData.payload,
            prevSnapshot,
            newSnapshot,
            (uuid) => this.world.getActorByUuid(uuid),
            pendingCallbacks,
          );

          // Fire onRep callbacks now that the new state has been applied.
          for (const [host, cbName] of pendingCallbacks) {
            (host as Record<string, () => void>)[cbName]();
          }

          if (unresolvedRefs.size > 0) {
            for (const [dotPath, referencedUuid] of unresolvedRefs) {
              this.addDanglingRef(actor.uuid, dotPath, referencedUuid);
            }
            console.debug(`[ClientNetWorld] Actor ${actor.constructor.name} has ${unresolvedRefs.size} unresolved references:`, [...unresolvedRefs.keys()]);
          }

          this.lastAppliedTick.set(transportId, packet.tick);

          // Only store the snapshot and ACK if the snapshot was successfully stored.
          // Otherwise withhold the ACK so the server does not advance its baseTick to a tick we cannot decode against.
          if (this.storeReceivedSnapshot(transportId, packet.tick, updateData.baseTick, newSnapshot)) {
            acksToSend.push({ transportId, ackType: 'updated' });
          }
        } else {
          console.warn(`[ClientNetWorld] Received update for unknown transport ID: ${transportId}`);
        }
      } else if (updateType === 'destroy') {
        const actor = this.transportIdToActor.get(transportId);
        if (actor) {
          this.removeDanglingRefsForOwner(actor.uuid);
          this.world.removeActors(actor);
          this.transportIdToActor.delete(transportId);
          this.lastAppliedTick.delete(transportId);
          this.receivedSnapshots.delete(transportId);
        }
      }
    }

    // Send acknowledgments back to server
    if (acksToSend.length > 0) {
      const ack: ReplicationAck = {
        tick: packet.tick,
        actors: acksToSend
      };

      if (this.gameSession) {
        this.gameSession.sendReplicationAck(ack);
      } else {
        console.warn('[ClientNetWorld] No game session attached; cannot send replication ack');
      }
    }
  }

  /**
   * Store a received state snapshot in the per-actor sliding window.
   * Returns `true` if the snapshot was stored, `false` if the window was full.
   *
   * When the window is full this method first tries to free space by evicting
   * snapshots the server has certainly discarded from its own ring buffer (any
   * entry more than MAX_CLIENT_SNAPSHOTS_PER_ACTOR ticks behind `tick`, excluding
   * `pinnedTick`). If the window is still full after that, the snapshot is not
   * stored and the caller must withhold the ACK so the server does not advance
   * its baseTick to a tick the client cannot decode against.
   *
   * Since ticks are always monotonically increasing (stale packets are dropped
   * before reaching this path), not adding when full is equivalent to evicting
   * the newest entry — the safest strategy because the server's next baseTick
   * jump lands on an older tick (the oldest survivor in its ring buffer).
   *
   * @param pinnedTick - The current baseTick, which must never be evicted.
   *   Pass -1 for the spawn path where there is no base.
   */
  private storeReceivedSnapshot(
    transportId: number,
    tick: number,
    pinnedTick: number,
    snapshot: Record<string, unknown>
  ): boolean {
    let tickMap = this.receivedSnapshots.get(transportId);
    if (!tickMap) {
      tickMap = new Map();
      this.receivedSnapshots.set(transportId, tickMap);
    }
    if (tickMap.size >= ClientNetWorld.MAX_CLIENT_SNAPSHOTS_PER_ACTOR) {
      // Evict snapshots the server has certainly already discarded. A snapshot
      // at tick T is gone from the server when tick - T >= MAX (both sides share
      // the same window size, so the server's oldest non-pinned cannot be older).
      for (const t of tickMap.keys()) {
        if (t !== pinnedTick && tick - t >= ClientNetWorld.MAX_CLIENT_SNAPSHOTS_PER_ACTOR) {
          tickMap.delete(t);
        }
      }
      if (tickMap.size >= ClientNetWorld.MAX_CLIENT_SNAPSHOTS_PER_ACTOR) {
        return false;
      }
    }
    tickMap.set(tick, snapshot);
    return true;
  }

  /**
   * Register an unresolved actor reference so it can be resolved when the target spawns.
   * If the same `(ownerUuid, dotPath)` pair already points at a different referenced UUID
   * (superseded reference), the old entry is removed from the inverted index first.
   */
  private addDanglingRef(ownerUuid: string, dotPath: string, referencedUuid: string): void {
    // Forward map: ownerUuid → (dotPath → referencedUuid)
    let pathMap = this.danglingForward.get(ownerUuid);
    if (!pathMap) {
      pathMap = new Map();
      this.danglingForward.set(ownerUuid, pathMap);
    }
    const oldRefUuid = pathMap.get(dotPath);
    if (oldRefUuid && oldRefUuid !== referencedUuid) {
      // Superseded reference — remove old entry from inverted index.
      this.removeFromInverted(oldRefUuid, ownerUuid, dotPath);
    }
    pathMap.set(dotPath, referencedUuid);

    // Inverted map: referencedUuid → (ownerUuid → Set<dotPath>)
    let ownerMap = this.danglingInverted.get(referencedUuid);
    if (!ownerMap) {
      ownerMap = new Map();
      this.danglingInverted.set(referencedUuid, ownerMap);
    }
    let pathSet = ownerMap.get(ownerUuid);
    if (!pathSet) {
      pathSet = new Set();
      ownerMap.set(ownerUuid, pathSet);
    }
    pathSet.add(dotPath);
  }

  /** Remove a single entry from the inverted index. */
  private removeFromInverted(referencedUuid: string, ownerUuid: string, dotPath: string): void {
    const ownerMap = this.danglingInverted.get(referencedUuid);
    if (!ownerMap) return;
    const pathSet = ownerMap.get(ownerUuid);
    if (pathSet) {
      pathSet.delete(dotPath);
      if (pathSet.size === 0) ownerMap.delete(ownerUuid);
    }
    if (ownerMap.size === 0) this.danglingInverted.delete(referencedUuid);
  }

  /**
   * Remove all dangling references where `ownerUuid` is the holder.
   * Called when an owner actor is destroyed so stale inverted-index entries are cleaned up.
   */
  private removeDanglingRefsForOwner(ownerUuid: string): void {
    const pathMap = this.danglingForward.get(ownerUuid);
    if (!pathMap) return;
    for (const [dotPath, referencedUuid] of pathMap) {
      this.removeFromInverted(referencedUuid, ownerUuid, dotPath);
    }
    this.danglingForward.delete(ownerUuid);
  }

  /**
   * Resolve all pending actor references that were waiting for `newActorUuid` to spawn.
   * Called immediately after each actor is added to the world.
   * Only the entries in the inverted index for `newActorUuid` are visited — O(pending refs).
   */
  private resolveDanglingReferences(newActorUuid: string): void {
    const ownerMap = this.danglingInverted.get(newActorUuid);
    if (!ownerMap) return;

    const referencedActor = this.world.getActorByUuid(newActorUuid);

    for (const [ownerUuid, dotPaths] of [...ownerMap]) {
      const ownerActor = this.world.getActorByUuid(ownerUuid);
      if (!ownerActor) {
        // Owner was destroyed before its reference could be resolved — clean up forward map.
        this.danglingForward.delete(ownerUuid);
        ownerMap.delete(ownerUuid);
        continue;
      }
      if (!referencedActor) continue; // shouldn't happen; defensive guard

      // Ordered list of (host, callbackName) pairs to fire after all assignments.
      // Using an array + seen Set preserves depth-first order while deduplicating
      // (e.g. multiple resolved elements of the same array fire onRepArr only once,
      // and only after all elements have been written).
      const pendingCallbacks: [any, string][] = [];
      const seenCallbacks = new Set<string>(); // `${ownerUuid}:${dotPath-to-host}.cbName`

      const scheduleCallback = (host: any, cbName: string, dedupeKey: string): void => {
        if (seenCallbacks.has(dedupeKey)) return;
        seenCallbacks.add(dedupeKey);
        if (typeof host[cbName] === 'function') pendingCallbacks.push([host, cbName]);
      };

      const resolveSegment = (h: any, seg: string): any => {
        const idxMatch = /^\[(\d+)\]$/.exec(seg);
        return idxMatch ? h?.[parseInt(idxMatch[1], 10)] : h?.[seg];
      };

      for (const dotPath of [...dotPaths]) {
        const parts = dotPath.split('.');
        const leafPart = parts[parts.length - 1];

        // Traverse from the owner actor to the direct parent of the leaf property.
        // Dot paths for component properties start with "$components.{replicationKey}"; resolve
        // those two segments via getReplicatedComponentMap() rather than a plain property access.
        // Array index segments have the form "[n]" and are resolved with a numeric key.
        const ancestorHosts: [any, string][] = [];
        let host: any = ownerActor;
        let startIndex = 0;
        let traversalOk = true;

        if (parts[0] === REPLICATION_COMPONENTS_KEY && parts.length >= 3) {
          const componentRepKey = parts[1];
          const component = ownerActor
            .getReplicatedComponentMap()
            .find(c => c.replicationKey === componentRepKey);
          if (!component) {
            traversalOk = false;
          } else {
            ancestorHosts.push([ownerActor, parts[0]], [ownerActor, parts[1]]);
            host = component;
            startIndex = 2;
          }
        }

        for (let i = startIndex; traversalOk && i < parts.length - 1; i++) {
          ancestorHosts.push([host, parts[i]]);
          host = resolveSegment(host, parts[i]);
          if (host == null) traversalOk = false;
        }
        if (!traversalOk) continue;

        // Assign the resolved actor to the leaf. Array index leaves write to host[n].
        const idxMatch = /^\[(\d+)\]$/.exec(leafPart);
        if (idxMatch) {
          host[parseInt(idxMatch[1], 10)] = referencedActor;
        } else {
          host[leafPart] = referencedActor;
        }

        // Schedule onRep callbacks depth-first (leaf first, then ancestors outward).
        // For array index leaves the callback is promoted to the parent array property
        // so onRepArr fires once after all elements are assigned, not per element.
        const callbackStartIndex = idxMatch ? ancestorHosts.length - 1 : ancestorHosts.length;
        if (!idxMatch) {
          const cbName = `onRep${leafPart.charAt(0).toUpperCase()}${leafPart.slice(1)}`;
          scheduleCallback(host, cbName, `${ownerUuid}:${dotPath}:${cbName}`);
        }
        for (let i = callbackStartIndex - 1; i >= 0; i--) {
          const [ah, prop] = ancestorHosts[i];
          const cbName = `onRep${prop.charAt(0).toUpperCase()}${prop.slice(1)}`;
          // Build a stable dedupe key from the host's dot path prefix.
          const hostPath = parts.slice(0, i + 1).join('.');
          scheduleCallback(ah, cbName, `${ownerUuid}:${hostPath}:${cbName}`);
        }

        // Remove the resolved entry from both maps.
        dotPaths.delete(dotPath);
        const forwardPaths = this.danglingForward.get(ownerUuid);
        if (forwardPaths) {
          forwardPaths.delete(dotPath);
          if (forwardPaths.size === 0) this.danglingForward.delete(ownerUuid);
        }

        console.debug(`[ClientNetWorld] Resolved dangling reference: ${ownerActor.constructor.name}.${dotPath} -> ${referencedActor.constructor.name}`);
      }

      // Fire all collected callbacks now that every actor reference has been written.
      for (const [host, cbName] of pendingCallbacks) host[cbName]();

      if (dotPaths.size === 0) ownerMap.delete(ownerUuid);
    }

    if (ownerMap.size === 0) this.danglingInverted.delete(newActorUuid);
  }

  public override clearNetObjects(): void {
    super.clearNetObjects();
    this.clearClientReplicationState();
    this.transportIdToActor.clear();
    this.danglingForward.clear();
    this.danglingInverted.clear();
    this.lastAppliedTick.clear();
    this.receivedSnapshots.clear();
  }

  public override shutdown(): void {
    this.clearClientReplicationState();
    super.shutdown();
  }

  private clearClientReplicationState(): void {
    this.transportIdToActor.clear();
    this.danglingForward.clear();
    this.danglingInverted.clear();
    this.lastAppliedTick.clear();
    this.receivedSnapshots.clear();
    super.doEndPlay();
  }
}
